import React, { useEffect, useMemo } from 'react';
import { useForm, useWatch, type FieldPath } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { SaleFormInput, FormInputInputType } from '@src/core/buyerApiClient';
import { useTranslate } from '@src/core/hooks';
import { formInputDateToDisplay, resolveFormInputValue, type FormInputValues } from '@src/domains/orders';

import { mapFormInputErrorsToFields } from '@src/features/orders/form-inputs/core/mapFormInputErrorsToFields';
import {
  tariffFormInputsSchema,
  type TariffFormValues,
} from '@src/features/orders/form-inputs/core/tariffFormInputsSchema';

type UseTariffFormInputsFormParams = {
  inputs: SaleFormInput[];
  values: FormInputValues;
  errors: Record<string, string[]>;
};

export const useTariffFormInputsForm = (params: UseTariffFormInputsFormParams) => {
  const { inputs, values, errors: storeErrors } = params;
  const { i18n } = useTranslate();

  const schema = useMemo(() => tariffFormInputsSchema({ inputs, t: i18n }), [inputs, i18n]);

  const defaultValues = useMemo(
    () =>
      inputs.reduce<TariffFormValues>((acc, input) => {
        const value = resolveFormInputValue(input, values);

        // The server returns dates as yyyy-mm-dd; the field masks them as dd/mm/yyyy.
        acc[input.hash] = input.inputType === FormInputInputType.Date ? formInputDateToDisplay(value) : value;

        return acc;
      }, {}),
    [inputs, values],
  );

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<TariffFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    // The schema is all strings, with no `transform`, so `z.input` and `z.output` match
    // and the plain `zodResolver` is enough.
    resolver: zodResolver(schema),
    defaultValues,
  });

  const watched = useWatch({ control });

  const canSubmit = useMemo(() => schema.safeParse(watched).success, [schema, watched]);

  const fieldErrors = useMemo(() => mapFormInputErrorsToFields(storeErrors, inputs), [storeErrors, inputs]);

  // The signature, not the list, is the dependency: `setError` re-renders, and depending
  // on the `storeErrors`/`inputs` references, recreated on every render by the caller,
  // would feed the effect back into an infinite loop.
  const fieldErrorsSignature = JSON.stringify(fieldErrors);

  useEffect(() => {
    fieldErrors.forEach(({ path, message }) =>
      setError(path as FieldPath<TariffFormValues>, { type: 'manual', message }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the signature covers the content
  }, [fieldErrorsSignature, setError]);

  const submit = (onValid: (collected: TariffFormValues) => void) =>
    handleSubmit((collected) => onValid(collected)) as (event?: React.BaseSyntheticEvent) => Promise<void>;

  return { control, errors, canSubmit, submit };
};

export default useTariffFormInputsForm;
